import { useEffect, useState } from 'react';

import { DualAxes } from '@ant-design/charts';
import { BarChartOutlined } from '@ant-design/icons';
import { Card, Empty, Segmented } from 'antd';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import { useTranslation } from 'react-i18next';

import { TurnProgressByDayData } from '../types';

dayjs.extend(isoWeek);

const BASE = 'scenes.kit.dashboard.components.dashboard_combo_chart';

type Granularity = 'day' | 'week';

interface DashboardComboChartProps {
  data?: TurnProgressByDayData;
  loading?: boolean;
  filterKey?: number;
}

function DashboardComboChart({
  data,
  loading,
  filterKey,
}: DashboardComboChartProps) {
  const { t, i18n } = useTranslation();
  const [granularity, setGranularity] = useState<Granularity>('day');

  // Vuelve a 'day' cada vez que se aplica o resetea el filtro
  useEffect(() => {
    setGranularity('day');
  }, [filterKey]);

  const isEmpty = !data || Object.keys(data).length === 0;

  const labelTotal = t(`${BASE}.series.total_shifts`);
  const labelFinished = t(`${BASE}.series.finished`);
  const labelRate = t(`${BASE}.series.compliance_rate`);
  const ttToComplete = t(`${BASE}.tooltip.to_complete`);
  const ttCompleted = t(`${BASE}.tooltip.completed`);
  const ttRate = t(`${BASE}.tooltip.compliance_rate`);

  /**
   * columnData — formato largo: 2 filas por período (una por serie de barras).
   * lineData   — 1 fila por período para la línea de cumplimiento.
   *
   * En modo 'day'  → label = DD/MM o MM/DD
   * En modo 'week' → acumula por semana ISO y label = S{ww} o W{ww}
   */
  const columnData: { day: string; value: number; category: string }[] = [];
  const lineData: { day: string; rate: number }[] = [];

  if (!isEmpty) {
    if (granularity === 'day') {
      Object.entries(data).forEach(([dateStr, row]) => {
        const d = dayjs(dateStr);
        const label =
          i18n.language === 'es' ? d.format('DD/MM') : d.format('MM/DD');
        const rate =
          row.toBeCompleted > 0
            ? Number(((row.completed / row.toBeCompleted) * 100).toFixed(0))
            : 0;

        columnData.push({
          day: label,
          value: row.toBeCompleted,
          category: labelTotal,
        });
        columnData.push({
          day: label,
          value: row.completed,
          category: labelFinished,
        });
        lineData.push({ day: label, rate });
      });
    } else {
      // Agrupa por semana ISO — acumula toBeCompleted y completed
      const weekMap = new Map<
        string,
        { toBeCompleted: number; completed: number }
      >();

      Object.entries(data).forEach(([dateStr, row]) => {
        const d = dayjs(dateStr);
        const weekNum = d.isoWeek();
        const prefix = i18n.language === 'es' ? 'Semana' : 'Week';
        const label = `${prefix} ${weekNum}`;

        const existing = weekMap.get(label) ?? {
          toBeCompleted: 0,
          completed: 0,
        };
        weekMap.set(label, {
          toBeCompleted: existing.toBeCompleted + row.toBeCompleted,
          completed: existing.completed + row.completed,
        });
      });

      weekMap.forEach((row, label) => {
        const rate =
          row.toBeCompleted > 0
            ? Number(((row.completed / row.toBeCompleted) * 100).toFixed(0))
            : 0;

        columnData.push({
          day: label,
          value: row.toBeCompleted,
          category: labelTotal,
        });
        columnData.push({
          day: label,
          value: row.completed,
          category: labelFinished,
        });
        lineData.push({ day: label, rate });
      });
    }
  }

  /**
   * Mapa día → datos originales para el tooltip.
   * Se reconstruye desde lineData que ya tiene rate calculado.
   */
  const byDay = Object.fromEntries(
    lineData.map((r, i) => [
      r.day,
      {
        rate: r.rate,
        toBeCompleted: columnData[i * 2]?.value ?? 0,
        completed: columnData[i * 2 + 1]?.value ?? 0,
      },
    ])
  );

  const hasData =
    columnData.some((d) => d.value > 0) || lineData.some((d) => d.rate > 0);

  const skeletonHeights = [60, 80, 40, 90, 55, 70, 45];

  const config = {
    xField: 'day',

    /**
     * Tooltip compartido disparado por la interacción raíz.
     * `items` como función recibe el datum del punto activo y construye
     * exactamente 3 items sin duplicados.
     */
    tooltip: {
      title: (datum: any) => datum.day,
      items: (datum: any) => {
        const row = byDay[datum.day] ?? {
          rate: 0,
          toBeCompleted: 0,
          completed: 0,
        };
        return [
          { name: ttRate, value: `${row.rate}%`, color: '#BD2229' },
          { name: ttToComplete, value: row.toBeCompleted, color: '#003087' },
          { name: ttCompleted, value: row.completed, color: '#00BFB3' },
        ];
      },
    },

    children: [
      /**
       * ── Barras agrupadas ──
       * Un único mark interval con colorField + group:true (= dodgeX).
       * La scale.color con domain explícito garantiza que cada categoría
       * tenga siempre su color fijo, sin importar el orden.
       */
      {
        type: 'interval' as const,
        data: columnData,
        yField: 'value',
        colorField: 'category',
        group: true,
        style: { maxWidth: 26 },
        scale: {
          y: {
            key: 'leftAxis',
            nice: true,
          },
          color: {
            domain: [labelTotal, labelFinished],
            range: ['#003087', '#00BFB3'],
          },
        },
        axis: {
          y: {
            title: t(`${BASE}.axis.shifts`),
            position: 'left',
          },
        },
        tooltip: {
          title: 'day',
          items: [
            (d: any) => {
              const row = byDay[d.day] ?? {
                rate: 0,
                toBeCompleted: 0,
                completed: 0,
              };
              return { name: ttRate, value: `${row.rate}%`, color: '#BD2229' };
            },
            (d: any) => {
              const row = byDay[d.day] ?? {
                rate: 0,
                toBeCompleted: 0,
                completed: 0,
              };
              return {
                name: ttToComplete,
                value: row.toBeCompleted,
                color: '#003087',
              };
            },
            (d: any) => {
              const row = byDay[d.day] ?? {
                rate: 0,
                toBeCompleted: 0,
                completed: 0,
              };
              return {
                name: ttCompleted,
                value: row.completed,
                color: '#00BFB3',
              };
            },
          ],
        },
      },

      /**
       * ── Línea de tasa de cumplimiento ──
       * `independent: true` garantiza su propio eje Y derecho sin interferir
       * con las barras. Sin mark `point` separado (evita el bug data:null).
       */
      {
        type: 'line' as const,
        data: lineData,
        yField: 'rate',
        style: {
          stroke: '#BD2229',
          lineWidth: 2,
        },
        scale: {
          y: {
            independent: true,
            nice: true,
            domainMin: 0,
            domainMax: 120,
          },
        },
        axis: {
          y: {
            title: t(`${BASE}.axis.compliance_rate`),
            position: 'right',
            labelFormatter: (v: number) => `${v}%`,
          },
        },
        labels: [
          {
            text: 'rate',
            formatter: (v: number) => `${v}%`,
            style: {
              fill: '#BD2229',
              fontSize: 11,
              fontWeight: 'bold',
              dy: -10,
            },
          },
        ],
        tooltip: {
          title: 'day',
          items: [
            (d: any) => {
              const row = byDay[d.day] ?? {
                rate: 0,
                toBeCompleted: 0,
                completed: 0,
              };
              return { name: ttRate, value: `${row.rate}%`, color: '#BD2229' };
            },
            (d: any) => {
              const row = byDay[d.day] ?? {
                rate: 0,
                toBeCompleted: 0,
                completed: 0,
              };
              return {
                name: ttToComplete,
                value: row.toBeCompleted,
                color: '#003087',
              };
            },
            (d: any) => {
              const row = byDay[d.day] ?? {
                rate: 0,
                toBeCompleted: 0,
                completed: 0,
              };
              return {
                name: ttCompleted,
                value: row.completed,
                color: '#00BFB3',
              };
            },
          ],
        },
      },
    ],

    /** Leyenda nativa de G2 deshabilitada — se renderiza manualmente abajo */
    legend: false,
  };

  const cardTitle = t(`${BASE}.title`);

  return (
    <Card title={cardTitle} styles={{ body: { paddingTop: 12 } }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
          gap: 8,
        }}
      >
        <span style={{ fontSize: 12, color: '#8c8c8c' }}>
          {t(`${BASE}.subtitle`)}
        </span>
        <Segmented
          shape="round"
          size="small"
          value={granularity}
          onChange={(val) => setGranularity(val as Granularity)}
          options={[
            { label: t(`${BASE}.segmented.day`), value: 'day' },
            { label: t(`${BASE}.segmented.week`), value: 'week' },
          ]}
        />
      </div>
      {loading ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
            height: 300,
            gap: 8,
            padding: '0 4px',
          }}
        >
          {skeletonHeights.map((h1, idx) => {
            const h2 = Math.round(h1 * 0.7);
            return (
              <div
                key={idx}
                style={{
                  flex: '1 1 0',
                  minWidth: 0,
                  maxWidth: 28,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  justifyContent: 'flex-end',
                  gap: 3,
                }}
              >
                <div
                  className="kit-dashboard__chart-skeleton-bar"
                  style={{ height: h1 }}
                />
                <div
                  className="kit-dashboard__chart-skeleton-bar kit-dashboard__chart-skeleton-bar--secondary"
                  style={{ height: h2 }}
                />
              </div>
            );
          })}
        </div>
      ) : isEmpty || !hasData ? (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: 300,
          }}
        >
          <Empty
            description={
              <span style={{ color: '#d9d9d9' }}>{t(`${BASE}.empty`)}</span>
            }
            image={
              <BarChartOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
            }
            imageStyle={{ height: 48, marginBottom: 4 }}
          />
        </div>
      ) : (
        <>
          {/* Leyenda personalizada — reemplaza la de G2 para evitar flechas */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 20,
              marginBottom: 8,
              flexWrap: 'wrap',
            }}
          >
            {[
              { color: '#BD2229', label: labelRate, marker: 'line' },
              { color: '#003087', label: labelTotal, marker: 'square' },
              { color: '#00BFB3', label: labelFinished, marker: 'square' },
            ].map(({ color, label, marker }) => (
              <div
                key={label}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: '#595959',
                }}
              >
                {marker === 'line' ? (
                  <svg width={16} height={12} style={{ flexShrink: 0 }}>
                    <line
                      x1={0}
                      y1={6}
                      x2={16}
                      y2={6}
                      stroke={color}
                      strokeWidth={2}
                    />
                    <circle cx={8} cy={6} r={3} fill={color} />
                  </svg>
                ) : (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 12,
                      height: 12,
                      borderRadius: 2,
                      background: color,
                      flexShrink: 0,
                    }}
                  />
                )}
                {label}
              </div>
            ))}
          </div>
          <DualAxes {...(config as any)} height={280} />
        </>
      )}
    </Card>
  );
}

export default DashboardComboChart;
